import React, { useCallback, useEffect, useMemo } from 'react';

import { useRouter } from 'next/router';
import styled, { css } from 'styled-components';

import WhitelabelIcon from '~/icons/components/WhitelabelIcon';
import featureNames from '~/shared/constants/featureFlags';
import { getFreeShippingThresholds } from '~/shared/utils/getFreeShippingThresholds';
import useGender from '~/shared/utils/useGender';
import useHasFeature from '~/shared/utils/useHasFeature';
import usePostRegCountdownEndTime from '~/shared/utils/usePostRegCountdownEndTime';
import {
  useAccountActions,
  useCustomerDetail,
  useMembership,
} from '~/techstyle-shared/react-accounts';
import { useAsset } from '~/techstyle-shared/react-assets';
import { useCart } from '~/techstyle-shared/react-cart';
import {
  HeaderAttachment,
  useCountdown,
} from '~/techstyle-shared/react-components';
import { usePromoActions } from '~/techstyle-shared/react-promos';
import { DiscountDto } from '~/techstyle-shared/react-types';
import { useDomain } from '~/techstyle-shared/redux-core';

const PromoProgressBannerWrapper = styled.div<{ $isTimerActive: boolean }>`
  z-index: 2;
  padding: 16px;
  margin-bottom: 12px;
  width: 100%;
  position: relative;
  --active-color: ${(props) => props.theme.colors.semanticColors.active};
  ${({ theme }) => theme.promoProgressBanner.wrapper}
`;

const Title = styled.h3`
  ${({ theme }) => theme.promoProgressBanner.title}
`;

const StepsContainer = styled.div`
  flex: 1;
  ${({ theme }) => theme.promoProgressBanner.stepsContainer}
`;

const ProgressBarContainer = styled.div<{ $isDisabled: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      opacity: 0.4;
      pointer-events: none;
    `}
`;

const ProgressBarWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StepLabelsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  position: relative;
  margin-top: 2px;
`;

const StepLabelsGrid = styled.div<{ columns: number }>`
  display: grid;
  grid-template-columns: ${(props) => `repeat(${props.columns}, 1fr)`};
  width: 100%;
  margin: 0 auto;
`;

const ProgressStepLabel = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  justify-self: center;
  white-space: pre-line;
  hyphens: auto;
  ${({ theme }) => theme.promoProgressBanner.stepLabel}
`;

const ProgressBar = styled.div`
  height: 8px;
  position: relative;
  flex: 1;
  ${({ theme }) => theme.promoProgressBanner.progressBar}
`;

const ProgressFill = styled.div<{ width: number }>`
  height: 100%;
  background-color: var(--active-color);
  width: ${(props) => props.width}%;
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const ProgressStep = styled.div<{ $isComplete: boolean }>`
  position: relative;
  margin: 0 auto;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Completed step: circle with white background */
  width: ${(props) => (props.$isComplete ? '18px' : '2px')};
  height: ${(props) => (props.$isComplete ? '18px' : '12px')};
  border-radius: ${(props) => (props.$isComplete ? '50%' : '0')};
  background-color: ${(props) =>
    props.$isComplete ? '#ffffff' : 'var(--active-color)'};
  ${({ theme }) => theme.promoProgressBanner.progressStep}
`;

const TicksOverlay = styled.div<{ totalSteps: number }>`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: grid;
  grid-template-columns: ${(props) => `repeat(${props.totalSteps}, 1fr)`};
  align-items: center;
  pointer-events: none;
`;

const TimerCountdownBox = styled.div`
  ${({ theme }) => theme.promoProgressBanner.timerBox}
`;

const TimerLabel = styled.span`
  flex-shrink: 0;
  ${({ theme }) => theme.promoProgressBanner.timerLabel}
`;

const TimerTime = styled.span`
  ${({ theme }) => theme.promoProgressBanner.timerCountdown}
`;

type PromoProgressBannerCustomVars = {
  /**
   * Whether the timer should be displayed while the
   * post reg timer is active
   */
  displayTimer: boolean;
  /**
   * The label displayed in the banner
   */
  mainLabel: string;
  /**
   * The label displayed in the timer (only used if displayTimer is true)
   */
  timerLabel: string;
  /**
   * Whether the banner should be in the disabled state for non-member carts after
   * an item has been added to the cart.
   */
  disableForNonMemberCart: boolean;
  /**
   * The label displayed when the banner is in the disabled state (only used if
   * `disableForNonMemberCart` is true and the cart is not a member cart)
   */
  disabledStateLabel: string;
  gender?: 'M' | 'F';
  /**
   * The promo code used for the first step, when empty the
   * free shipping threshold is used instead. This can be a comma
   * separated list of promo codes.
   */
  step1Promo: string;
  /**
   * The label displayed in the first step
   */
  step1Label: string;
  /**
   * The promo code used for the second step, when empty the
   * free shipping threshold is used instead. This can be a comma
   * separated list of promo codes.
   */
  step2Promo: string;
  /**
   * The label displayed in the second step
   */
  step2Label: string;
  /**
   * The promo code used for the third step, when empty the
   * free shipping threshold is used instead. This can be a comma
   * separated list of promo codes.
   */
  step3Promo: string;
  /**
   * The label displayed in the third step
   */
  step3Label: string;
  /**
   * The promo code used for the fourth step, when empty the
   * free shipping threshold is used instead. This can be a comma
   * separated list of promo codes.
   */
  step4Promo: string;
  /**
   * The label displayed in the fourth step
   */
  step4Label: string;
  /**
   * The customer detail value to be added so that promos
   * are properly applied.
   */
  customerDetailPromoIdentifier?: string;
};

/**
 * Hook that provides shipping threshold logic based on user membership status
 * and cart state. Returns information about free shipping eligibility and thresholds.
 */
function useShippingThreshold() {
  const { isVip } = useMembership();
  const { tld } = useDomain();
  // @ts-ignore The options are not typed correctly
  const { isVipCart, subtotalMinusDiscount = 0 } = useCart({
    isLoginRequired: true,
  });
  const { countDownEndTime } = usePostRegCountdownEndTime();
  const { isTimerActive: isInPostRegWindow } = useCountdown(countDownEndTime);

  const freeShippingThresholdConfig = getFreeShippingThresholds({
    isVip,
    isVipCart,
    isInPostRegWindow,
  });

  const freeShippingThresholdMatch =
    freeShippingThresholdConfig[tld] ?? freeShippingThresholdConfig['.com'];
  const freeShippingThreshold =
    freeShippingThresholdMatch?.shippingThreshold || 0;
  const belowThreshold = subtotalMinusDiscount < freeShippingThreshold;
  const priceUntilFreeShipping = freeShippingThreshold - subtotalMinusDiscount;
  const isFreeShippingEligible = !belowThreshold;

  return {
    freeShippingThreshold,
    belowThreshold,
    priceUntilFreeShipping,
    isFreeShippingEligible,
    isInPostRegWindow,
  };
}

const usePromoProgressBannerCustomerDetail = () => {
  const {
    value,
    networkStatus: { isUpToDate },
  } = useCustomerDetail('promo_progress_banner');
  const { updateCustomerDetail } = useAccountActions();
  const { checkMembershipPromos } = usePromoActions();

  const resetCustomerDetail = useCallback(async () => {
    // @ts-ignore Typing incorrect for this action
    await updateCustomerDetail({
      name: 'promo_progress_banner',
      value: '',
    });
    await checkMembershipPromos({ forceCheck: true });
  }, [updateCustomerDetail, checkMembershipPromos]);

  const setCustomerDetail = useCallback(
    async (value: string) => {
      // @ts-ignore Typing incorrect for this action
      await updateCustomerDetail({
        name: 'promo_progress_banner',
        value,
      });
      await checkMembershipPromos({ forceCheck: true });
    },
    [updateCustomerDetail, checkMembershipPromos]
  );

  return {
    value,
    isUpToDate,
    resetCustomerDetail,
    setCustomerDetail,
  };
};

const PromoTimer = ({ timerLabel }: { timerLabel: string }) => {
  const { countDownEndTime } = usePostRegCountdownEndTime();
  const { minutes, seconds, padNumber } = useCountdown(countDownEndTime);

  return (
    <TimerCountdownBox data-autotag="ppb-timer-container">
      <TimerLabel>{timerLabel}</TimerLabel>
      <TimerTime>
        {padNumber(minutes)}:{padNumber(seconds)}
      </TimerTime>
    </TimerCountdownBox>
  );
};

function isStepComplete({
  promoCodes,
  discounts,
  isFreeShippingEligible,
}: {
  promoCodes: Set<string>;
  discounts: DiscountDto[];
  isFreeShippingEligible: boolean;
}) {
  if (!promoCodes || promoCodes.size === 0) {
    return isFreeShippingEligible;
  }
  const isPromoApplied = discounts.some(
    (discount) => discount.amount > 0 && promoCodes.has(discount.promoCode)
  );
  return isPromoApplied;
}

const createPromoStep = ({
  promoCode,
  label,
  discounts,
  isFreeShippingEligible,
  forceIncomplete = false,
}: {
  promoCode: string;
  label: string;
  discounts: DiscountDto[];
  isFreeShippingEligible: boolean;
  forceIncomplete?: boolean;
}) => {
  const promoCodesSet = new Set(
    promoCode ? promoCode.split(',').map((code) => code.trim()) : []
  );
  return {
    promoCodesSet,
    label,
    isComplete: forceIncomplete
      ? false
      : isStepComplete({
          promoCodes: promoCodesSet,
          discounts,
          isFreeShippingEligible,
        }),
  };
};

type ProgressBarDisplayProps = {
  isDisabled: boolean;
  steps: Array<{
    promoCodesSet: Set<string>;
    label: string;
    isComplete: boolean;
  }>;
};

const ProgressBarDisplay = ({
  steps,
  isDisabled = false,
}: ProgressBarDisplayProps) => {
  const completedSteps = steps.filter((_, index) => {
    return steps.slice(0, index + 1).every((step) => step.isComplete);
  }).length;

  // Show initial 5% progress even when no steps are completed
  const progressPercentage =
    completedSteps > 0 ? (completedSteps / steps.length) * 100 : 5;

  return (
    <ProgressBarContainer $isDisabled={isDisabled}>
      <ProgressBarWrapper>
        <ProgressBar>
          <ProgressFill width={progressPercentage} />
          <TicksOverlay totalSteps={steps.length}>
            {steps.map((_, index) => {
              const isStepComplete = steps
                .slice(0, index + 1)
                .every((s) => s.isComplete);
              return (
                <ProgressStep
                  key={index}
                  $isComplete={isStepComplete}
                  data-autotag={`ppb-step-${index + 1}-indicator-${
                    isStepComplete ? 'complete' : 'incomplete'
                  }`}
                >
                  {isStepComplete && (
                    <WhitelabelIcon
                      iconName="checkCircle"
                      size={16}
                      color="black"
                    />
                  )}
                </ProgressStep>
              );
            })}
          </TicksOverlay>
        </ProgressBar>
      </ProgressBarWrapper>
      <StepLabelsContainer>
        <StepLabelsGrid columns={steps.length}>
          {steps.map((step, index) => {
            return (
              <ProgressStepLabel
                key={index}
                data-autotag={`ppb-step-${index + 1}-label`}
              >
                {step.label}
              </ProgressStepLabel>
            );
          })}
        </StepLabelsGrid>
      </StepLabelsContainer>
    </ProgressBarContainer>
  );
};

const getPromoProgressBannerAssetFilter =
  (gender: 'M' | 'F', isInPostRegWindow) => () => (asset) => {
    const { gender: assetGender, displayTimer } = asset.options
      .customVars as PromoProgressBannerCustomVars;
    const genderFilter = assetGender ? assetGender === gender : true;
    const timerFilter = displayTimer
      ? displayTimer === isInPostRegWindow
      : true;

    return genderFilter && timerFilter;
  };

export const PromoProgressBanner = () => {
  const { isFreeShippingEligible, isInPostRegWindow } = useShippingThreshold();
  const {
    resetCustomerDetail,
    setCustomerDetail,
    isUpToDate: isBannerCustomerDetailRecordUpToDate,
    value: ppbCustomerDetailRecord,
  } = usePromoProgressBannerCustomerDetail();
  const { gender } = useGender();
  // Without this, the filter is recreated every time the timer from `useCountdown` changes
  const assetFilter = useMemo(
    () => getPromoProgressBannerAssetFilter(gender, isInPostRegWindow),
    [gender, isInPostRegWindow]
  );
  const {
    data: promoProgressBannerAssetData,
    networkStatus: promoProgressBannerAssetNetworkStatus,
  } = useAsset(
    'Promo_Progress_Banner',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    { multi: true, filter: assetFilter } as any
  );

  // @ts-ignore The options are not typed correctly
  const { discounts, itemCount, isVipCart } = useCart({
    isLoginRequired: true,
  });
  const { isVip } = useMembership();

  const currentSteps = useMemo(() => {
    if (!promoProgressBannerAssetData?.assets.length) {
      return null;
    }
    return promoProgressBannerAssetData.assets[0].options
      .customVars as PromoProgressBannerCustomVars;
  }, [promoProgressBannerAssetData]);

  const forceIncomplete = useMemo(() => {
    return (
      currentSteps?.disableForNonMemberCart &&
      !isVipCart &&
      !isVip &&
      itemCount > 0
    );
  }, [currentSteps, isVipCart, isVip, itemCount]);

  const steps = useMemo(() => {
    if (!currentSteps) {
      return [];
    }

    // We want to show the free shipping step as complete before any items are
    // added to cart and the post reg timer is active
    const forceFreeShippingOnFirstStep = itemCount === 0 && isInPostRegWindow;

    const stepsWithDetails = [
      createPromoStep({
        promoCode: currentSteps.step1Promo,
        label: currentSteps.step1Label,
        discounts,
        isFreeShippingEligible: forceFreeShippingOnFirstStep
          ? true
          : isFreeShippingEligible,
        forceIncomplete,
      }),
      createPromoStep({
        promoCode: currentSteps.step2Promo,
        label: currentSteps.step2Label,
        discounts,
        isFreeShippingEligible,
        forceIncomplete,
      }),
      createPromoStep({
        promoCode: currentSteps.step3Promo,
        label: currentSteps.step3Label,
        discounts,
        isFreeShippingEligible,
        forceIncomplete,
      }),
      createPromoStep({
        promoCode: currentSteps.step4Promo,
        label: currentSteps.step4Label,
        discounts,
        isFreeShippingEligible,
        forceIncomplete,
      }),
    ];

    return stepsWithDetails.filter((step) => step.label);
  }, [
    currentSteps,
    discounts,
    forceIncomplete,
    isFreeShippingEligible,
    itemCount,
    isInPostRegWindow,
  ]);

  useEffect(() => {
    // When the feature is enabled for the customer and they have an available PPB asset, update the customer detail record accordingly
    if (currentSteps && isBannerCustomerDetailRecordUpToDate) {
      const { customerDetailPromoIdentifier } = currentSteps;
      if (
        customerDetailPromoIdentifier &&
        customerDetailPromoIdentifier !== ppbCustomerDetailRecord
      ) {
        setCustomerDetail(customerDetailPromoIdentifier);
      } else if (!customerDetailPromoIdentifier && ppbCustomerDetailRecord) {
        resetCustomerDetail();
      }
      // When the feature is enabled for the customer, but they don't have an available PPB asset, reset the customer detail record
      // we aren't doing this in `PromoProgressBannerRenderer` because if the feature is disabled we don't need to reset customer detail records
      // assuming the promos are disabled when the bar is unavailable
    } else if (
      ppbCustomerDetailRecord &&
      promoProgressBannerAssetNetworkStatus.isUpToDate &&
      !currentSteps
    ) {
      resetCustomerDetail();
    }
  }, [
    ppbCustomerDetailRecord,
    currentSteps,
    isBannerCustomerDetailRecordUpToDate,
    setCustomerDetail,
    resetCustomerDetail,
    promoProgressBannerAssetNetworkStatus,
  ]);

  if (!currentSteps) {
    return null;
  }

  const isTimerActive =
    currentSteps.displayTimer && isInPostRegWindow && !forceIncomplete;

  return (
    <PromoProgressBannerWrapper $isTimerActive={isTimerActive}>
      <Title
        data-autotag={`ppb-title-${forceIncomplete ? 'disabled' : 'enabled'}`}
      >
        {forceIncomplete
          ? currentSteps.disabledStateLabel
          : currentSteps.mainLabel}
      </Title>
      {/* NOTE FOR FUTURE:
        While SXF is using a progress bar display, the most recent mocks for FL use a series of
        boxes instead. We can switch between the components that are rendered here based on brand later.
        Some additional asset options can also be added, such as a "display type" option. 
      */}
      <StepsContainer>
        <ProgressBarDisplay steps={steps} isDisabled={forceIncomplete} />
      </StepsContainer>
      {isTimerActive && <PromoTimer timerLabel={currentSteps.timerLabel} />}
    </PromoProgressBannerWrapper>
  );
};

export const PromoProgressBannerRenderer = () => {
  const router = useRouter();
  const isBannerAllowed = useMemo(() => {
    if (router.pathname === '/') {
      return false;
    }

    return [
      '/products/[identifier]',
      '/builder/category-page',
      '/builder/collection-page',
      '/builder/sale-page',
    ].includes(router.pathname);
  }, [router]);

  // Don't check the feature flag until on a valid page
  const { enabled: isBannerEnabled } = useHasFeature(
    isBannerAllowed ? featureNames.PROMO_PROGRESS_BANNER : undefined
  );

  if (!isBannerEnabled) {
    return null;
  }

  return (
    <HeaderAttachment>
      <PromoProgressBanner />
    </HeaderAttachment>
  );
};
